import { useEffect, useMemo, useState } from 'react'
import { useGetProjectContactsQuery } from '../../../react-query/members/query/use-get-project-contacts-query'
import { useGetProjectMembersQuery } from '../../../react-query/members/query/use-get-project-members-query'
import { useGetProjectTeamsQuery } from '../../../react-query/members/query/use-get-project-teams-query'
import { copyProjectMembersTabOptions } from '../constants'
import {
  buildDefaultSelectionByTab,
  getCheckboxStateByTab,
  getNormalizedSelectionByTab,
  getSelectedItemIdsByTab,
  hasSelectionChanged,
  toggleIncludeTeamGroupMembersInSelection,
  toggleSelectionCheckboxInSelection,
  toggleSelectionItemInSelection
} from './members-selection-model'

const defaultIsIncludedByTab = {
  [copyProjectMembersTabOptions.MEMBERS]: false,
  [copyProjectMembersTabOptions.TEAMS]: false,
  [copyProjectMembersTabOptions.CONTACTS]: false
}

const useMembersSelectionHook = (projectId) => {
  const [selectionByTab, setSelectionByTab] = useState(
    buildDefaultSelectionByTab
  )
  const [isIncludedByTab, setIsIncludedByTab] = useState(defaultIsIncludedByTab)

  const { data: members, isLoading: isLoadingMembers } =
    useGetProjectMembersQuery(projectId)
  const { data: teams, isLoading: isLoadingTeams } =
    useGetProjectTeamsQuery(projectId)
  const { data: contacts, isLoading: isLoadingContacts } =
    useGetProjectContactsQuery(projectId)

  const itemsByTab = useMemo(
    () => ({
      [copyProjectMembersTabOptions.MEMBERS]: Array.isArray(members?.content)
        ? members?.content
        : [],
      [copyProjectMembersTabOptions.TEAMS]: Array.isArray(teams) ? teams : [],
      [copyProjectMembersTabOptions.CONTACTS]: Array.isArray(contacts)
        ? contacts
        : []
    }),
    [contacts, members, teams]
  )

  useEffect(() => {
    setSelectionByTab(buildDefaultSelectionByTab())
    setIsIncludedByTab(defaultIsIncludedByTab)
  }, [projectId])

  useEffect(() => {
    setSelectionByTab((currentSelectionByTab) => {
      const normalizedSelectionByTab = getNormalizedSelectionByTab(
        currentSelectionByTab,
        itemsByTab
      )

      const hasChanged = hasSelectionChanged(
        currentSelectionByTab,
        normalizedSelectionByTab
      )

      return hasChanged ? normalizedSelectionByTab : currentSelectionByTab
    })
  }, [itemsByTab])

  const normalizedSelectionByTab = useMemo(
    () => getNormalizedSelectionByTab(selectionByTab, itemsByTab),
    [itemsByTab, selectionByTab]
  )

  const selectedItemIdsByTab = useMemo(
    () => getSelectedItemIdsByTab(normalizedSelectionByTab, itemsByTab),
    [itemsByTab, normalizedSelectionByTab]
  )

  const checkboxStateByTab = useMemo(
    () => getCheckboxStateByTab(normalizedSelectionByTab, itemsByTab),
    [itemsByTab, normalizedSelectionByTab]
  )

  const replaceSelectionByTab = (nextSelectionByTab) => {
    setSelectionByTab(
      getNormalizedSelectionByTab(nextSelectionByTab, itemsByTab)
    )
  }

  const toggleSelectionCheckbox = (tab, nextValue) => {
    setSelectionByTab((currentSelectionByTab) =>
      toggleSelectionCheckboxInSelection(
        currentSelectionByTab,
        itemsByTab,
        tab,
        nextValue
      )
    )
  }

  const toggleSelectionItem = (tab, itemId) => {
    setSelectionByTab((currentSelectionByTab) =>
      toggleSelectionItemInSelection(
        currentSelectionByTab,
        itemsByTab,
        tab,
        itemId
      )
    )
  }

  const toggleIsIncludedByTab = (tab, nextValue) => {
    setIsIncludedByTab((current) => ({
      ...current,
      [tab]:
        typeof nextValue === 'function' ? nextValue(current[tab]) : !!nextValue
    }))
  }

  const toggleIncludeTeamGroupMembers = (nextValue) => {
    setSelectionByTab((currentSelectionByTab) =>
      toggleIncludeTeamGroupMembersInSelection(
        currentSelectionByTab,
        itemsByTab,
        nextValue
      )
    )
  }

  return {
    members: members?.content,
    teams,
    contacts,
    isLoadingContacts,
    isLoadingMembers,
    isLoadingTeams,
    itemsByTab,
    selectionByTab: normalizedSelectionByTab,
    selectedItemIdsByTab,
    checkboxStateByTab,
    isIncludedByTab,
    replaceSelectionByTab,
    toggleIsIncludedByTab,
    toggleSelectionCheckbox,
    toggleSelectionItem,
    toggleIncludeTeamGroupMembers
  }
}

export default useMembersSelectionHook
